import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import createCachedSelector from 're-reselect';
import { useSelector } from 'react-redux';

import Global from '../../../store/global-selectors';
import { mapOrdersFiltersToRequestBody } from '../../../services/filtering/api-param-map-service';
import { getOrderStatusFromViewName } from '../../../services/api/order-status-from-view-service';
import { getIsBatchDetailsPage } from '../../../store/batches/batches.helpers';
import { getSelectedViewFromPathName } from '../../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { getInsuranceProviderById } from '../../../enums/insurance-providers';

import messages from './messages';

const buildOrdersGridRequestFiltersSelector = createCachedSelector(
  (_, filters) => filters,
  (_, __, pathname) => pathname,
  (state) => Global.selectSellerActiveStores(state),
  (state) => Global.selectSellerTimeZoneIANA(state),
  (state) => Global.selectOrdersFilteringActiveFilterView(state),
  (state) => Global.selectBatchDetailsAppliedFilters(state),
  (state) => Global.selectSelectedOrdersIds(state),
  (state) => Global.selectBatchDetailsBatch(state),
  (state) => Global.selectOrdersNavSearchData(state),
  (state) => Global.selectSelectedOrdersAreAllOrdersSelected(state),
  (
    filters,
    pathname,
    stores,
    sellerTimeZone,
    activeFilterView,
    appliedFilters,
    selectedOrdersIds,
    batchDetails,
    searchTerm,
    areAllSelected,
  ) => {
    const statusView = getSelectedViewFromPathName(pathname, stores);
    const { storeGuid } = statusView || {};
    const requestStatusViewId = statusView?.parentId || statusView?.id;
    const isOnBatchDetailsPage = getIsBatchDetailsPage();
    const filterReq = isOnBatchDetailsPage ? appliedFilters || [] : activeFilterView.appliedFilters || [];
    const filterRequest = mapOrdersFiltersToRequestBody(filterReq, sellerTimeZone);
    const daFilters = filters || filterRequest;
    const isSuperSelect = selectedOrdersIds?.length === 0 && areAllSelected;
    const status = getOrderStatusFromViewName(requestStatusViewId);

    return {
      ...daFilters,
      storeGuid: storeGuid ? [storeGuid] : undefined,
      orderGridStatus: isSuperSelect ? status : undefined,
      fulfillmentPlanIds: isSuperSelect ? undefined : selectedOrdersIds,
      fulfillmentPlanSetId: isOnBatchDetailsPage ? batchDetails.fulfillmentPlanSetId : undefined,
      searchTerm: isSuperSelect ? searchTerm.searchTerm : undefined,
    };
  },
)((_, filters, pathname) => `${filters}_${pathname}`);

export const useOrdersGridRequestFilters = (filters, pathname) => {
  return useSelector((state) => buildOrdersGridRequestFiltersSelector(state, filters, pathname));
};

export const useRecommendationNames = (uniqueServiceIds, uniqueInsuranceProviderIds, uniqueServiceCollectionGuids) => {
  const intl = useIntl();
  const services = useSelector((state) => Global.selectSellerServicesByIds(state, { serviceIds: uniqueServiceIds }));

  return useMemo(() => {
    const names = [];

    uniqueInsuranceProviderIds.forEach((insuranceProviderId) => {
      const insuranceProvider = getInsuranceProviderById(insuranceProviderId);
      if (insuranceProvider) {
        names.push(insuranceProvider.name);
      }
    });

    services.forEach((service) => {
      names.push(service.name);
    });

    if (uniqueServiceCollectionGuids?.length > 0) {
      names.push(intl.formatMessage(messages.rateShopper));
    }

    return names;
  }, [intl, services, uniqueInsuranceProviderIds, uniqueServiceCollectionGuids]);
};
